import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ToastNotification";
import { api } from "@/lib/api";
import { Upload, X, Download, Eye } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import VisualizadorArquivo from "./visualizadorArquivo";

interface Anexo {
  id: number;
  nome_arquivo: string;
  caminho_arquivo: string;
  tipo_arquivo?: string;
  data_upload: string;
}

interface AnexosProps {
  protocoloId?: number;
  quantidadeExames?: number;
  endpointBase?: string; // Endpoint base para anexos (ex: '/api/protocolo-prontuario-generalista' ou '/api/protocolo-encaminhamento-cirurgico')
}

export const Anexos = ({ protocoloId, quantidadeExames = 0, endpointBase = '/api/protocolo-prontuario-generalista' }: AnexosProps) => {
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [arquivoSelecionado, setArquivoSelecionado] = useState<{
    url: string;
    nome: string;
    tipo?: string;
  } | null>(null);

  const loadAnexos = useCallback(async () => {
    if (!protocoloId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`${endpointBase}/${protocoloId}/anexos`);
      if (response?.result) {
        setAnexos(response.result);
      }
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
    } finally {
      setLoading(false);
    }
  }, [protocoloId, endpointBase]);

  // Carregar anexos existentes
  useEffect(() => {
    if (protocoloId) {
      console.log('📎 [Anexos] Carregando anexos para protocoloId:', protocoloId, 'endpoint:', endpointBase);
      loadAnexos();
    } else {
      console.log('⚠️ [Anexos] protocoloId não fornecido:', { protocoloId, endpointBase });
      setAnexos([]);
    }
  }, [protocoloId, loadAnexos, endpointBase]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    if (!protocoloId) {
      toast.warning("Salve o protocolo antes de anexar documentos.");
      return;
    }

    // Verificar limite
    if (quantidadeExames === 0) {
      toast.warning("Solicite exames antes de anexar documentos.");
      return;
    }

    if (anexos.length + selectedFiles.length > quantidadeExames) {
      toast.error(`Você pode anexar no máximo ${quantidadeExames} documento(s) (quantidade de exames solicitados).`);
      return;
    }

    // Fazer upload de cada arquivo
    for (const file of selectedFiles) {
      await uploadFile(file);
    }

    // Limpar input
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    
    if (!protocoloId) {
      toast.warning("Salve o protocolo antes de anexar documentos.");
      return;
    }

    // Verificar limite
    if (quantidadeExames === 0) {
      toast.warning("Solicite exames antes de anexar documentos.");
      return;
    }

    if (anexos.length + droppedFiles.length > quantidadeExames) {
      toast.error(`Você pode anexar no máximo ${quantidadeExames} documento(s) (quantidade de exames solicitados).`);
      return;
    }

    // Fazer upload de cada arquivo
    for (const file of droppedFiles) {
      await uploadFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const uploadFile = async (file: File) => {
    if (!protocoloId) return;

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);

      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const token = localStorage.getItem('auth_token');

      const response = await fetch(
        `${BASE_URL}${endpointBase}/${protocoloId}/anexos`,
        {
          method: 'POST',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
          credentials: 'include'
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.message || error?.mensagem || 'Erro ao fazer upload');
      }

      const result = await response.json();
      
      // Recarregar lista de anexos
      await loadAnexos();
      
      toast.success(`Arquivo "${file.name}" anexado com sucesso!`);
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error?.message || "Não foi possível anexar o arquivo.");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (anexoId: number, nomeArquivo: string) => {
    if (!protocoloId) return;

    try {
      setLoading(true);
      await api.delete(`${endpointBase}/${protocoloId}/anexos/${anexoId}`);
      
      // Recarregar lista
      await loadAnexos();
      
      toast.success(`Arquivo "${nomeArquivo}" removido com sucesso!`);
    } catch (error: any) {
      console.error('Erro ao remover anexo:', error);
      toast.error("Não foi possível remover o arquivo.");
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (anexo: Anexo): string => {
    const BASE_URL = import.meta.env.VITE_API_BASE_URL;
    const caminhoEncoded = encodeURIComponent(anexo.caminho_arquivo);
    return `${BASE_URL}/api/upload/download/${caminhoEncoded}`;
  };

  const downloadFile = async (anexo: Anexo) => {
    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const token = localStorage.getItem('auth_token');
      
      // O caminho precisa ser codificado para URL
      // O caminho vem como "protocolos/1/nome_arquivo" e precisa ser codificado
      const caminhoEncoded = encodeURIComponent(anexo.caminho_arquivo);
      
      console.log('📥 [Anexos] Baixando arquivo:', {
        caminho: anexo.caminho_arquivo,
        caminhoEncoded,
        url: `${BASE_URL}/api/upload/download/${caminhoEncoded}`
      });
      
      const response = await fetch(
        `${BASE_URL}/api/upload/download/${caminhoEncoded}`,
        {
          method: 'GET',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Anexos] Erro na resposta:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`Erro ao baixar arquivo: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Arquivo vazio recebido');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = anexo.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Arquivo "${anexo.nome_arquivo}" baixado com sucesso!`);
    } catch (error: any) {
      console.error('❌ [Anexos] Erro ao baixar arquivo:', error);
      toast.error(error?.message || "Não foi possível baixar o arquivo.");
    }
  };

  const visualizarArquivo = async (anexo: Anexo) => {
    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const token = localStorage.getItem('auth_token');
      const caminhoEncoded = encodeURIComponent(anexo.caminho_arquivo);
      
      // Para visualizar, precisamos obter o arquivo e criar uma URL blob
      const response = await fetch(
        `${BASE_URL}/api/upload/download/${caminhoEncoded}`,
        {
          method: 'GET',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao carregar arquivo para visualização');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      setArquivoSelecionado({
        url,
        nome: anexo.nome_arquivo,
        tipo: anexo.tipo_arquivo
      });
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('❌ [Anexos] Erro ao visualizar arquivo:', error);
      toast.error("Não foi possível abrir o arquivo para visualização.");
    }
  };

  const fecharVisualizador = () => {
    if (arquivoSelecionado?.url) {
      window.URL.revokeObjectURL(arquivoSelecionado.url);
    }
    setVisualizadorAberto(false);
    setArquivoSelecionado(null);
  };

  const maxAnexos = quantidadeExames;
  const anexosRestantes = maxAnexos - anexos.length;
  const podeAnexar = protocoloId && quantidadeExames > 0 && anexosRestantes > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Anexos</Label>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            {anexos.length} / {maxAnexos} arquivo{maxAnexos !== 1 ? 's' : ''}
          </Badge>
          {quantidadeExames === 0 && (
            <span className="text-xs text-muted-foreground">
              Solicite exames para anexar documentos
            </span>
          )}
        </div>
      </div>

      {podeAnexar ? (
        <div 
          className={`border-2 border-dashed rounded-lg p-8 text-center hover:bg-accent/50 cursor-pointer transition-colors ${
            uploading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <label htmlFor="file-upload" className="cursor-pointer">
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">
              {uploading ? 'Enviando...' : 'Arraste arquivos ou clique para enviar'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              PDF, JPG, PNG (máx 10MB) - {anexosRestantes} documento(s) restante(s)
            </p>
          </label>
          <input
            id="file-upload"
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handleFileSelect}
            disabled={uploading || !podeAnexar}
          />
        </div>
      ) : (
        <div className="border-2 border-dashed rounded-lg p-8 text-center bg-muted/50">
          <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
          <p className="text-sm font-medium text-muted-foreground">
            {!protocoloId 
              ? "Salve o protocolo antes de anexar documentos"
              : quantidadeExames === 0
              ? "Solicite exames antes de anexar documentos"
              : "Limite de anexos atingido"}
          </p>
        </div>
      )}

      <div>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Carregando anexos...
          </div>
        ) : anexos.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Nenhum anexo ainda
          </div>
        ) : (
          <div className="space-y-2">
            {anexos.map((anexo) => (
              <div
                key={anexo.id}
                className="flex items-center justify-between p-3 border rounded-lg bg-background"
              >
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{anexo.nome_arquivo}</p>
                    <p className="text-xs text-muted-foreground">
                      {anexo.tipo_arquivo?.toUpperCase()} • {new Date(anexo.data_upload).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {(anexo.tipo_arquivo?.toLowerCase() === 'pdf' || 
                      ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(anexo.tipo_arquivo?.toLowerCase() || '')) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => visualizarArquivo(anexo)}
                        className="h-8 w-8 p-0"
                        title="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadFile(anexo)}
                      className="h-8 w-8 p-0"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(anexo.id, anexo.nome_arquivo)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      disabled={loading}
                      title="Remover"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visualizador de Arquivo */}
      {arquivoSelecionado && (
        <VisualizadorArquivo
          arquivoUrl={arquivoSelecionado.url}
          nomeArquivo={arquivoSelecionado.nome}
          tipoArquivo={arquivoSelecionado.tipo}
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </div>
  );
};

export default Anexos;

